import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";

import { FormGroup } from "@cofano/cck";
import { ChangeShipAndCharterDocument } from "__generated__/view/charters/graphql/charter.mutations.generated";
import useCreateOrExistingCharterFormFields, {
	charterDefaultValues,
	ExistingCharterType,
} from "view/charters/components/form/useCreateOrExistingCharterFormFields";
import { intl } from "LocaleWrapper";
import { FormDialog } from "components/dialogs/Dialogs";
import { Language } from "utils";
import FormField from "components/cck/FormField";
import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import { CreateOrExistingPurchaseCharterSchema } from "view/charters/utils/charter.schema";
import { shipSelectOptionSchema } from "view/ships/utils/ship.schema";
import { CreateOrSelectCharterFormCargoFragment } from "__generated__/graphql";

const UpdateCharterShipDialogSchema = z.object({
	...CreateOrExistingPurchaseCharterSchema.shape,
	ship: shipSelectOptionSchema,
});

type UpdateCharterShipDialogProps = {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
	voyage: {
		id: number;
		ship: ShipSelectOption;
		charter?: ExistingCharterType | null;
		cargos: CreateOrSelectCharterFormCargoFragment[]
	}
}

type UpdateCharterShipDialogInput = z.input<typeof UpdateCharterShipDialogSchema>
type UpdateCharterShipDialogOutput = z.output<typeof UpdateCharterShipDialogSchema>

const UpdateCharterShipDialog = ({
	open,
	onClose,
	voyage,
	refetch,
}: UpdateCharterShipDialogProps) => {

	const { control, handleSubmit, formState, watch, setValue } = useForm<UpdateCharterShipDialogInput, unknown, UpdateCharterShipDialogOutput>({
		resolver: zodResolver(UpdateCharterShipDialogSchema),
		defaultValues: {
			ship: voyage.ship,
			charter: voyage.charter ? {
				...charterDefaultValues,
				subtype: "EXISTING" as const,
				existing: voyage.charter,
			} : charterDefaultValues,
		} satisfies UpdateCharterShipDialogInput,
	});

	const [ ship ] = watch([ "ship" ]);

	const { charterInput, charterFields, createContract } = useCreateOrExistingCharterFormFields({
		control: control,
		setValue: setValue,
		watch: watch,
		formState: formState,
		ship: ship ?? undefined,
		voyage: voyage,
		cargos: voyage.cargos,
		createContractDisabled: false,
	});

	const [ updateShipAndCharter, { loading } ] = useMutation(ChangeShipAndCharterDocument);

	const save = async (data: UpdateCharterShipDialogOutput) => {
		await updateShipAndCharter({
			variables: {
				voyageId: voyage.id,
				shipId: data.ship.id,
				charterInput: charterInput,
				createContract: createContract,
			},
		});

		refetch();
		onClose();
	};

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "SHIP_AND_CHARTER_EDIT" })}
			form="shipEditForm"
			loading={loading}
			maxWidth="md">
			<React.Fragment>
				<form onSubmit={(event) => void handleSubmit(save)(event)} id="shipEditForm">
					<FormGroup
						label={Language("SHIP")}
						labelPlacement="left-center"
						overflow="hidden"
						required
					>
						<FormField errors={formState.errors} name="ship">
							<Controller
								name="ship"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<ShipSelect { ...field} clearable={false} />
								)}
							/>
						</FormField>
					</FormGroup>

					<h4 style={{ fontSize: 16, fontWeight: 600, margin: "24px 0 16px" }}>
						{intl.formatMessage({ id: "CHARTER" })}
					</h4>
					{charterFields}
				</form>
			</React.Fragment>
		</FormDialog>
	);
};

export default UpdateCharterShipDialog;
